import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import type {
  Doctor,
  Service,
  Variante,
  BookingResponse,
  CedulaLookupResponse,
  IdType,
} from '@booking/shared';
import { ID_RULES, validateIdNumber } from '@booking/shared';
import { api, ApiError } from '../api/client';
import { formatLongDate, formatHora } from '../lib/dates';
import { useTrafficSource } from '../hooks/useTrafficSource';
import { Turnstile } from './Turnstile';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const PRIVACY_URL = import.meta.env.VITE_PRIVACY_URL;

/**
 * Per-id-type validation. Cédula = strict 9 digits; everything else lenient
 * since formats vary (passport, DIMEX 11-12 digits, minor 10 digits).
 */
const ID_TYPE_LABEL: Record<IdType, string> = {
  cedula: 'Cédula de Identidad',
  extranjero: 'Extranjero (pasaporte)',
  dimex: 'DIMEX (residencia)',
  menor: 'Menor de Edad',
};

const ID_TYPE_PLACEHOLDER: Record<IdType, string> = {
  cedula: '9 dígitos',
  extranjero: 'Número de pasaporte',
  dimex: 'Número de DIMEX',
  menor: 'Número de identificación',
};

type IdRule = { min: number; max: number; numeric: boolean };
const ID_ERROR_MESSAGES: Record<string, (r: IdRule) => string> = {
  cedula_format: () => 'La cédula de identidad debe tener exactamente 9 dígitos',
  too_short: (r) => `Mínimo ${r.min} caracteres`,
  too_long: (r) => `Máximo ${r.max} caracteres`,
  not_numeric: () => 'Solo se permiten números',
};


const schema = z
  .object({
    idType: z.enum(['cedula', 'extranjero', 'dimex', 'menor']),
    firstName: z.string().min(1, 'Requerido').max(50),
    lastName: z.string().min(1, 'Requerido').max(50),
    secondLastName: z.string().max(50).optional().default(''),
    idNumber: z.string().min(1, 'Requerido'),
    email: z.string().email('Email inválido'),
    phone: z
      .string()
      .regex(/^\+?\d{8,15}$/, 'Teléfono inválido (8-15 dígitos)'),
  })
  .superRefine((data, ctx) => {
    const rules = ID_RULES[data.idType];
    const idError = validateIdNumber(data.idType, data.idNumber);
    if (idError) {
      const buildMessage = ID_ERROR_MESSAGES[idError];
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['idNumber'],
        message: buildMessage ? buildMessage(rules) : 'Número de identificación inválido',
      });
    }
    // CR nationals always have two surnames; foreigners may have one.
    if (data.idType !== 'extranjero' && !data.secondLastName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['secondLastName'],
        message: 'Requerido',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

type Props = {
  doctor: Doctor;
  service: Service;
  date: string;
  time: string;
  /* depilación láser: las zonas escogidas y lo que duran en total */
  zonas?: string[];
  minutos?: number;
  /* la opción escogida dentro del servicio (Mirena, T de plata…) */
  variante?: Variante;
  onSuccess: (confirmationCode: string, reservaId: string) => void;
  onBack: () => void;
};

export function ConfirmForm({ doctor, service, date, time, zonas, minutos, variante, onSuccess, onBack }: Props) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { idType: 'cedula' },
  });
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [lookupCedula, setLookupCedula] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const source = useTrafficSource();

  const idType = watch('idType');

  // TSE lookup — only for "cedula" type, only when blur fires with 9 digits.
  const tse = useQuery({
    queryKey: ['cedula', lookupCedula],
    queryFn: async (): Promise<CedulaLookupResponse> => {
      const res = await api.lookupCedula(lookupCedula!);
      if (res && 'found' in res && res.found) {
        const current = getValues();
        if (!current.firstName) setValue('firstName', res.firstName, { shouldDirty: false });
        if (!current.lastName) setValue('lastName', res.lastName, { shouldDirty: false });
        if (!current.secondLastName) {
          setValue('secondLastName', res.secondLastName, { shouldDirty: false });
        }
      }
      return res;
    },
    enabled: !!lookupCedula,
    staleTime: 24 * 60 * 60_000,
    retry: false,
  });

  const onIdNumberBlur = () => {
    const current = getValues();
    if (current.idType === 'cedula' && /^\d{9}$/.test(current.idNumber)) {
      if (current.idNumber !== lookupCedula) setLookupCedula(current.idNumber);
    }
  };

  const onSubmit = async (data: FormValues) => {
    // Block submit until the captcha is solved (only when configured).
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setServerError('Completá la verificación de seguridad antes de continuar.');
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      const res: BookingResponse = await api.book({
        doctorId: doctor.id,
        serviceId: service.id,
        date,
        time,
        patient: data,
        source,
        turnstileToken: turnstileToken ?? undefined,
        zonas,
        variante: variante?.nombre,
      });
      if (res.ok) onSuccess(res.confirmationCode, res.bookingId);
      else setServerError(res.message || 'No pudimos reservar. Intentá de nuevo.');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'slot_taken') {
          setServerError('Ese horario acaba de ser reservado. Por favor regresá y escogé otro.');
        } else {
          // Worker returns friendly Spanish messages for rate_limited / captcha / sikumed_error.
          setServerError(e.message || 'No pudimos reservar. Intentá de nuevo.');
        }
      } else {
        setServerError('Error de conexión. Por favor intentá de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const tseFound = tse.data && 'found' in tse.data && tse.data.found;
  const tseRateLimited =
    tse.data && 'error' in tse.data && tse.data.error === 'rate_limited';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="confirm-form">
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <button type="button" className="back-btn" onClick={onBack}>‹ Volver</button>
        <h2 style={{ margin: 0 }}>Confirmar Reserva</h2>
        <span style={{ width: 60 }} />
      </header>

      <div className="summary">
        <p style={{ margin: 0 }}>{formatLongDate(date)}</p>
        <p style={{ margin: '8px 0', fontSize: 18 }}>
          <strong>{formatHora(time)}</strong> · {minutos ?? service.durationMinutes} min
        </p>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          {variante?.nombre ?? service.name}
          {zonas && zonas.length > 0 && service.porZonas && (
            <>
              {': '}
              {service.porZonas.zonas
                .filter((z) => zonas.includes(z.id))
                .map((z) => z.nombre.toLowerCase())
                .join(', ')}
            </>
          )}
        </p>
      </div>

      <label>
        Tipo de identificación <span style={{ color: 'var(--text-muted)' }}>(Requerido)</span>
        <select {...register('idType')}>
          {(Object.keys(ID_TYPE_LABEL) as IdType[]).map((k) => (
            <option key={k} value={k}>{ID_TYPE_LABEL[k]}</option>
          ))}
        </select>
      </label>

      <label>
        Número de identificación <span style={{ color: 'var(--text-muted)' }}>(Requerido)</span>
        <input
          {...register('idNumber', { onBlur: onIdNumberBlur })}
          inputMode={ID_RULES[idType].numeric ? 'numeric' : 'text'}
          maxLength={ID_RULES[idType].max}
          placeholder={ID_TYPE_PLACEHOLDER[idType]}
        />
        {idType === 'cedula' && tse.isFetching && (
          <em style={{ color: 'var(--text-muted)' }}>Buscando en TSE…</em>
        )}
        {idType === 'cedula' && tseFound && (
          <em style={{ color: 'var(--primary)' }}>✓ Verificado con TSE</em>
        )}
        {idType === 'cedula' && tseRateLimited && (
          <em style={{ color: 'var(--text-muted)' }}>
            Demasiadas consultas. Podés llenar los campos manualmente.
          </em>
        )}
        {errors.idNumber && <em>{errors.idNumber.message}</em>}
      </label>

      <label>
        Nombre <span style={{ color: 'var(--text-muted)' }}>(Requerido)</span>
        <input {...register('firstName')} autoComplete="given-name" />
        {errors.firstName && <em>{errors.firstName.message}</em>}
      </label>

      <label>
        Primer Apellido <span style={{ color: 'var(--text-muted)' }}>(Requerido)</span>
        <input {...register('lastName')} autoComplete="family-name" />
        {errors.lastName && <em>{errors.lastName.message}</em>}
      </label>

      <label>
        Segundo Apellido{' '}
        <span style={{ color: 'var(--text-muted)' }}>
          {idType === 'extranjero' ? '(Opcional)' : '(Requerido)'}
        </span>
        <input {...register('secondLastName')} autoComplete="additional-name" />
        {errors.secondLastName && <em>{errors.secondLastName.message}</em>}
      </label>

      <label>
        Email <span style={{ color: 'var(--text-muted)' }}>(Requerido)</span>
        <input type="email" {...register('email')} autoComplete="email" />
        {errors.email && <em>{errors.email.message}</em>}
      </label>

      <label>
        Número de teléfono <span style={{ color: 'var(--text-muted)' }}>(Requerido)</span>
        <input
          type="tel"
          {...register('phone')}
          autoComplete="tel"
          placeholder="+506 8888 7777"
        />
        {errors.phone && <em>{errors.phone.message}</em>}
      </label>

      {TURNSTILE_SITE_KEY && (
        <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} />
      )}

      {serverError && <div className="error">{serverError}</div>}

      <button type="submit" className="submit" disabled={submitting}>
        {submitting ? 'Reservando...' : 'Confirmar Reserva'}
      </button>

      <p className="consent">
        Al confirmar, aceptás el tratamiento de tus datos personales conforme a la{' '}
        {PRIVACY_URL ? (
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            política de privacidad
          </a>
        ) : (
          'política de privacidad'
        )}{' '}
        de Clínica Alena (Ley 8968).
      </p>
    </form>
  );
}
